export enum SessionStorageKeys {
  NotSet = '',
  StartQuery = 'REDLAB-Start-Query',
  //the Google session (services/Oidc/oidcAuthProvider.ts): the id_token and the profile claims, as the
  //oidc-client-ts User serializes them. The library's own "oidc." entries for a sign-in in flight (the PKCE
  //verifier) live in LOCAL storage and are swept by clearStaleState at every sign-in start
  OidcUser = 'REDLAB-Oidc-User',
  //the organization sign-in session (an allow-listed OpenID Connect issuer), kept apart from the Google one
  OidcOrgUser = 'REDLAB-Oidc-OrgUser',
  //Google: set before a silent (prompt=none) round trip, on a cold load and when a renewal failed, so that a
  //session with nothing behind it at Google goes to the welcome screen instead of round-tripping on every
  //load; lifted only by a landing whose token has time left, and by logout
  OidcSilentAttempted = 'REDLAB-Oidc-SilentAttempted',
  //the organization sign-in's own silent-attempt guard: one provider's attempt must not use up the other's
  OidcOrgSilentAttempted = 'REDLAB-Oidc-OrgSilentAttempted',
  //Google only: set before the one extra round trip with prompt=consent that fetches a refresh token
  OidcConsentRetried = 'REDLAB-Oidc-ConsentRetried',
  //the org unit an organization switch was heading for when it needed a new Google sign-in
  OidcPendingOrgUnit = 'REDLAB-Oidc-PendingOrgUnit',
  //the same for the organization sign-in
  OidcOrgPendingOrgUnit = 'REDLAB-Oidc-OrgPendingOrgUnit',
  //the allow-listed issuer an organization sign-in is in flight with (services/Oidc/oidcAuthProvider.ts): its endpoints
  //and our client id there, as the API's login lookup answered them, so the redirect landing can finish it
  OidcIssuerInfo = 'REDLAB-Oidc-IssuerInfo',
  //which non-Microsoft provider started the sign-in that is landing (the IdentityProviderType value). Together
  //with the root path and the code (or error) in the query it is what makes a page load a landing of ours
  OidcPendingProvider = 'REDLAB-Oidc-PendingProvider',
  //the path and query a Google or organization sign-in started from (one sign-in is in flight per tab, so the
  //two providers share it). Written at the first redirect of a sign-in, kept across the consent retry and a
  //landing without a sign-in (prompt=none found no session), consumed by the landing that completes, removed
  //by a load that is not a landing and by logout
  OidcReturnUrl = 'REDLAB-Oidc-ReturnUrl',
  //a project's journey update in flight (components/Project/projectStorage.ts): the project and the newer
  //package version the user left for in the Store. Checked on each load of that project; removed once the project
  //is on that package
  ProjectJourneyUpdateStarted = 'REDLAB-Project-JourneyUpdateStarted',
  //the signal a responsible person typed in an inquiry and has not sent yet (components/Project/projectStorage.ts): a
  //prefix, the tenant id, the user id and the inquiry task's id follow. Sent as a comment on the task when the inquiry
  //is completed
  ProjectInquirySignal = 'REDLAB-Project-InquirySignal',
}

export function setSessionStorageData(name: SessionStorageKeys, value: string) {
  try {
    if (name === SessionStorageKeys.NotSet) return;
    window.sessionStorage.setItem(name, value);
  } catch {
    // no-op
  }
}

export function getSessionStorageData(name: SessionStorageKeys): string | undefined {
  try {
    if (name === SessionStorageKeys.NotSet) return;
    const val = window.sessionStorage.getItem(name);
    if (!val) return undefined;

    return val;
  } catch {
    return undefined;
  }
}

export function removeSessionStorageData(name: SessionStorageKeys) {
  try {
    if (name === SessionStorageKeys.NotSet) return;
    window.sessionStorage.removeItem(name);
  } catch {
    // no-op
  }
}

export function removeAllSessionStorageData() {
  try {
    window.sessionStorage.clear();
  } catch{
    // no-op
  }
}
